import React, { useEffect, useRef, useState } from "react";
import { Volume2, Play, Pause, Square, SkipBack, SkipForward } from "lucide-react";
import { useTTS } from "@/context/TTSContext";
import { T } from "@/context/LanguageContext";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

export function ListenControl() {
  const tts = useTTS();
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  if (!tts) return null;

  return (
    <div ref={wrap} className="relative">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 text-white/90 transition-colors" data-testid="listen-btn">
        <Volume2 className="h-4 w-4" />
        <span className="text-[13px]">Listen</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-[60] mt-2 w-72 rounded-lg border border-border bg-white p-4 shadow-2xl" data-testid="listen-panel">
          {!tts.supported ? (
            <p className="text-sm text-muted-foreground">Text-to-speech isn't supported in this browser.</p>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-2 text-navy">
                <Volume2 className="h-4 w-4" />
                <span className="font-semibold text-sm"><T>Listen to this page</T></span>
              </div>
              <div className="flex items-center justify-center gap-1.5 mb-4">
                <IconBtn onClick={tts.prev} testid="tts-prev"><SkipBack className="h-4 w-4" /></IconBtn>
                {tts.status === "playing" ? (
                  <IconBtn primary onClick={tts.pause} testid="tts-pause"><Pause className="h-5 w-5" /></IconBtn>
                ) : (
                  <IconBtn primary onClick={tts.play} testid="tts-play"><Play className="h-5 w-5" /></IconBtn>
                )}
                <IconBtn onClick={tts.stop} testid="tts-stop"><Square className="h-4 w-4" /></IconBtn>
                <IconBtn onClick={tts.next} testid="tts-next"><SkipForward className="h-4 w-4" /></IconBtn>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground"><T>Speed</T></span>
                <div className="flex gap-1">
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      onClick={() => tts.setRate(s)}
                      className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
                        tts.rate === s ? "bg-navy text-white" : "bg-parchment text-navy hover:bg-navy/10"
                      }`}
                      data-testid={`tts-speed-${s}`}
                    >
                      {s}×
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, onClick, primary, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center justify-center rounded-full transition-all ${
        primary ? "h-11 w-11 bg-oldglory-red text-white hover:bg-oldglory-dark shadow-md" : "h-9 w-9 bg-parchment text-navy hover:bg-navy/10"
      }`}
    >
      {children}
    </button>
  );
}

// Inline listen button for article sections
export function InlineListen({ paragraphs, label = "Listen", testid = "inline-listen-btn" }) {
  const tts = useTTS();
  if (!tts || !tts.supported) return null;
  const start = () => {
    tts.setContent(paragraphs);
    tts.stop();
    setTimeout(() => tts.play(), 60);
  };
  return (
    <button
      onClick={start}
      className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy transition-all hover:border-oldglory-red hover:text-oldglory-red"
      data-testid={testid}
    >
      <Volume2 className="h-4 w-4" /> <T>{label}</T>
    </button>
  );
}
